/**
 * +quests: side-quest state (docs/design/zones.md "Side quests"): what a hero may take from whom,
 * how field kills advance accepted quests (collect quests roll their item drops here, on the hero's
 * kill counter so the browser's prediction and the server agree), and the XP an outgrown quest
 * still pays. Pure; the ops that pay rewards live in sidequests.ts (they need field.ts).
 */
import { type SideQuestDef, SIDE_QUESTS, SIDE_QUESTS_AFTER_STEP, ZONES, sideQuestById, sideQuestTurnInId } from '@keystone/content';
import type { BountyState, HeroCharacter, SideQuestState } from './types';

/** Most side quests a hero may carry at once. */
export const QUEST_LOG_MAX = 20;
/** How close (yd) the saved position must be to an NPC to take or hand in a quest. */
export const QUEST_REACH_YD = 14;
/** A quest goes grey (trivial) once the hero is this many levels above it. */
export const QUEST_GREY_GAP = 3;

export const emptySideQuests = (): SideQuestState => ({ active: {}, done: {} });

/** The hero's side quests (empty for saves that have none yet). Read-only use. */
export function sideQuestsOf(ch: Pick<HeroCharacter, 'quests'> | null | undefined): SideQuestState {
  return ch?.quests ?? emptySideQuests();
}

/**
 * XP a quest of level `questLevel` pays a hero of `heroLevel`: full up to 2 levels over it; from
 * QUEST_GREY_GAP over (grey), half, then −15% per further level, down to 20%.
 */
export function sideQuestXp(xp: number, questLevel: number, heroLevel: number): number {
  const gap = Math.floor(heroLevel) - Math.floor(questLevel);
  const mult = gap < QUEST_GREY_GAP ? 1 : Math.max(0.2, 0.5 - 0.15 * (gap - QUEST_GREY_GAP));
  return Math.round(xp * mult);
}

/** The quest is grey (trivial) for this hero. */
export const sideQuestGrey = (questLevel: number, heroLevel: number): boolean => Math.floor(heroLevel) - Math.floor(questLevel) >= QUEST_GREY_GAP;

/** +spawn: the Slime Hollows' side quests open once Granny Pell has sent the hero off (the first main-quest step). */
export const HOLLOWS_SIDE_QUESTS_AFTER_STEP = 'mv_main_0_pell';
const EARLY_AREAS: ReadonlySet<string> = new Set(['slime_hollows']); // +spawn

/**
 * The zone's main quest has reached the step that opens the side quests (talking to the Elder);
 * +spawn: quests in the Slime Hollows (`areaId`) open earlier, after Granny Pell.
 */
export function sideQuestsOpen(b: BountyState, zoneId = 'mossvale', areaId?: string): boolean {
  const z = ZONES[zoneId];
  if (!z) return true;
  const step = areaId && EARLY_AREAS.has(areaId) ? HOLLOWS_SIDE_QUESTS_AFTER_STEP : SIDE_QUESTS_AFTER_STEP; // +spawn
  const at = z.chain.findIndex((s) => s.id === step);
  return at < 0 || (b.chain[zoneId] ?? 0) > at;
}

/**
 * Where a quest stands for this hero:
 *  - 'hidden': its chain predecessor is not handed in yet, or the side quests are not open;
 *  - 'locked': the hero is below its minLevel (the giver shows a grey "!");
 *  - 'available' / 'active' / 'ready' (enough kills / items, or a talk quest) / 'done'.
 */
export type SideQuestStatus = 'hidden' | 'locked' | 'available' | 'active' | 'ready' | 'done';

export function sideQuestStatus(ch: Pick<HeroCharacter, 'quests' | 'bounties' | 'zoneId'>, def: SideQuestDef, heroLevel: number): SideQuestStatus {
  const q = sideQuestsOf(ch);
  if (q.done[def.id]) return 'done';
  const have = q.active[def.id];
  if (have !== undefined) return def.kind === 'talk' || have >= def.count ? 'ready' : 'active';
  if (!sideQuestsOpen(ch.bounties, ch.zoneId, def.areaId)) return 'hidden'; // +spawn: per area
  if (def.requires && !q.done[def.requires]) return 'hidden';
  if (heroLevel < def.minLevel) return 'locked';
  return 'available';
}

/** One quest's progress after some kills (the HUD pops collected items). */
export interface SideQuestProgress {
  id: string;
  progress: number;
  count: number;
  /** Items picked up by these kills (collect quests). */
  gained: number;
}

/** Deterministic 0..1 roll for kill number `n` (the hero's lifetime kill counter) and a quest. */
export function questDropRoll(questId: string, n: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < questId.length; i++) h = Math.imul(h ^ questId.charCodeAt(i), 0x01000193);
  h = (h ^ Math.imul(n + 1, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Accepted kills advance every active side quest that counts them: 'kill' quests by one, 'collect'
 * quests by one when the drop roll (questDropRoll on `firstKill + i`) lands. Mutates `ch.quests`.
 * `firstKill` is the hero's kill counter before these kills (the same on client and server).
 */
export function sideQuestKills(ch: HeroCharacter, kills: readonly { npcId: string }[], firstKill: number): SideQuestProgress[] {
  const q = ch.quests;
  if (!q || !Object.keys(q.active).length) return [];
  const out = new Map<string, SideQuestProgress>();
  kills.forEach((k, i) => {
    for (const id of Object.keys(q.active).sort()) {
      const def = sideQuestById(id);
      if (!def || def.kind === 'talk' || !def.npcIds.includes(k.npcId)) continue;
      const have = q.active[id]!;
      if (have >= def.count) continue;
      if (def.kind === 'collect' && questDropRoll(id, firstKill + i) >= (def.item?.chance ?? 1)) continue;
      q.active[id] = have + 1;
      const row = out.get(id) ?? { id, progress: 0, count: def.count, gained: 0 };
      row.progress = have + 1;
      row.gained += def.kind === 'collect' ? 1 : 0;
      out.set(id, row);
    }
  });
  return [...out.values()];
}

/** The NPC a quest is handed in to. */
export const questTurnInNpc = (def: SideQuestDef): string => sideQuestTurnInId(def);

/** Every side quest, for UIs that list them. */
export const allSideQuests = (): readonly SideQuestDef[] => SIDE_QUESTS;
